import { Typography } from '@mui/material'
import {
  ErrorText,
  WarningCircleIcon,
  WarningContainer,
  WarningText,
} from 'components/Message'
import {
  Button,
  LoadingSpinner,
  Modal,
  Select,
  TextInput,
} from 'eezy-components'
import { countries } from 'helpers/countries'
import { daysInMonth, isAdult, months } from 'helpers/dates'
import { isValidUrl } from 'helpers/strings'
import cookie from 'js-cookie'
import { observer } from 'mobx-react'
import { useSnackbar } from 'notistack'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { AccountSetupStore, AuthStore, ProfileEndpointsStore } from 'stores'
import { useInstance } from 'util/di'
import ChangesSubmitted from './ChangesSubmitted'
import CloseAccount from './CloseAccount'

import PortfolioLinkIcon from 'assets/icons/marketplaces/portfolio-link.svg'

import {
  usePatchApiV1ContributorApplicationsUserIdSubmitApplication,
  usePatchApiV1ContributorApplicationsUserIdWithdrawApplication,
} from 'api'
import { Session } from 'types'

import { captureError, getErrorMsg } from 'helpers/error'
import { ApplicationeRejectedReason } from 'types/user'

import * as S from './styled'

type Props = {
  session: Session
}

export enum Modals {
  ChangesSubmitted = 'changes-submitted',
  CloseAccount = 'close-account',
}

type IFormInput = {
  first_name: string
  last_name: string
  address_street_line_1: string
  address_state: string
  country: string
  address_postal_code: string
  dob_month: string
  dob_day: string
  dob_year: string
}

type PortfolioLink = {
  id: number
  url: string
}

/**
 * Interpolated translations:
 *
 * i18n-tasks-use t('application_rejected.modal.are_you_sure')
 * i18n-tasks-use t('application_rejected.modal.changes_submitted')
 * i18n-tasks-use t('application_rejected.modal.changes_submitted_info')
 * i18n-tasks-use t('application_rejected.modal.close_account')
 * i18n-tasks-use t('application_rejected.modal.close_account_info')
 * i18n-tasks-use t('application_rejected.modal.close_account_question')
 * i18n-tasks-use t('application_rejected.modal.close_account_question_info')
 * i18n-tasks-use t('application_rejected.modal.close_your_account')
 * i18n-tasks-use t('application_rejected.modal.content')
 * i18n-tasks-use t('application_rejected.modal.title')
 * i18n-tasks-use t('application_rejected.modal.warning')
 */
const ApplicationRejectedModal = ({ session }: Props): JSX.Element => {
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const navigate = useNavigate()

  const [isOpen, setOpen] = useState(true)
  const [activeModal, setActiveModal] = useState<Modals | undefined>(undefined)

  const { clearSession } = useInstance<AuthStore>('AuthStore')
  const { fetchUserProfile, loadingUserProfile, userProfile } =
    useInstance<ProfileEndpointsStore>('ProfileEndpointsStore')
  const { updateContributorApplication, updatingApplication } =
    useInstance<AccountSetupStore>('AccountSetupStore')

  const { mutate: closeContributorAccount, loading: closingAccount } =
    usePatchApiV1ContributorApplicationsUserIdWithdrawApplication({
      user_id: session?.id || '',
    })

  const { mutate: onboardUser, loading: onboardingUser } =
    usePatchApiV1ContributorApplicationsUserIdSubmitApplication({
      user_id: session?.id || '',
    })

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    setValue,
    watch,
    getValues,
    setError,
    clearErrors,
    reset,
  } = useForm<IFormInput>({
    mode: 'onChange',
    defaultValues: {
      first_name: '',
      last_name: '',
      address_street_line_1: '',
      address_state: '',
      country: '',
      address_postal_code: '',
      dob_month: '',
      dob_day: '',
      dob_year: '',
    },
  })

  const rawLinks: string[] = userProfile?.portfolio_links ?? []

  const [links, setLinks] = useState<PortfolioLink[]>(
    rawLinks.length > 0
      ? rawLinks.map((url, i) => ({ id: i + 1, url }))
      : [{ id: 1, url: '' }]
  )
  const [linkErrors, setLinkErrors] = useState<Record<number, string>>({})

  const hasLinks = useMemo(() => {
    return links.some(link => link.url)
  }, [links])

  const totalLinksCount = useMemo(() => {
    const otherCount = links.length
    return otherCount
  }, [links])

  const addAnotherLink = () => {
    setLinks(cur => {
      const lastId = cur[cur.length - 1]?.id ?? 0
      return [...cur, { id: lastId + 1, url: '' }]
    })
  }

  const watchMonth = watch('dob_month')
  const watchYear = watch('dob_year')
  const watchDay = watch('dob_day')

  useEffect(() => {
    fetchUserProfile(session?.id || '')
  }, [session, fetchUserProfile])

  useEffect(() => {
    const { dob_day, dob_month, dob_year } = getValues()
    if (dob_day && dob_month && dob_year) {
      if (!isAdult(dob_year, dob_month, dob_day)) {
        setError('dob_year', {
          type: 'manual',
          message: t('errors.must_be_18'),
        })
      } else {
        clearErrors('dob_year')
      }
    }
  }, [clearErrors, getValues, setError, t, watchDay, watchMonth, watchYear])

  useEffect(() => {
    if (userProfile) {
      reset({
        first_name: userProfile.first_name || '',
        last_name: userProfile.last_name || '',
        address_street_line_1: userProfile.address?.address_line_one || '',
        address_state: userProfile.address?.state || '',
        country: userProfile.address?.country || '',
        address_postal_code: userProfile.address?.zip_code || '',
        dob_month: userProfile.dob_month || '',
        dob_day: userProfile.dob_day || '',
        dob_year: userProfile.dob_year || '',
      })

      if (
        userProfile.portfolio_links &&
        userProfile.portfolio_links.length > 0
      ) {
        setLinks(
          userProfile.portfolio_links.map((url, i) => ({
            id: i + 1,
            url,
          }))
        )
      }
    }
  }, [userProfile, reset])

  const applicationSoftRejections = useMemo(() => {
    if (!session) return []

    return session.application_rejection_reasons.map(
      reason => reason.required_field
    )
  }, [session])
  const needsName = useMemo(() => {
    return applicationSoftRejections.some(
      reason => reason === ApplicationeRejectedReason.ApplicantName
    )
  }, [applicationSoftRejections])
  const needsBirthdate = useMemo(() => {
    return applicationSoftRejections.some(
      reason => reason === ApplicationeRejectedReason.Birthdate
    )
  }, [applicationSoftRejections])
  const needsPortfolioLink = useMemo(() => {
    return applicationSoftRejections.some(
      reason => reason === ApplicationeRejectedReason.PortfolioLink
    )
  }, [applicationSoftRejections])
  const needsAddress = useMemo(() => {
    return applicationSoftRejections.some(
      reason => reason === ApplicationeRejectedReason.Address
    )
  }, [applicationSoftRejections])
  const doesNotPassNameValidation = useCallback(() => {
    if (needsName) {
      return Object.keys(errors).length > 0
    }
    return false
  }, [errors, needsName])
  const doesNotPassAddressValidation = useCallback(() => {
    if (needsAddress) {
      return Object.keys(errors).length > 0
    }
    return false
  }, [errors, needsAddress])
  const doesNotPassDOBValidation = useCallback(() => {
    if (needsBirthdate) {
      return (
        Object.keys(errors).length > 0 ||
        !watchMonth ||
        !watchDay ||
        !watchYear ||
        Boolean(errors.dob_year?.message)
      )
    }
    return false
  }, [errors, needsBirthdate, watchDay, watchMonth, watchYear])
  const doesNotPassLinkValidation = useCallback(() => {
    if (needsPortfolioLink) {
      return Object.keys(errors).length > 0 || !hasLinks
    }
    return false
  }, [errors, hasLinks, needsPortfolioLink])

  const formattedCountries = useMemo(() => {
    return countries.map(country => {
      return {
        label: country,
        value: country,
      }
    })
  }, [])

  const formattedMonths = useMemo(() => {
    return months.map(month => {
      return {
        label: month,
        value: month,
      }
    })
  }, [])

  const formattedDays = useMemo(
    () =>
      Array.from({ length: 31 }, (_, i) => {
        const day = String(i + 1)
        return {
          label: day,
          value: day,
        }
      }),
    []
  )

  const formattedYears = useMemo(() => {
    const currentYear = new Date().getFullYear()
    const startYear = 1900
    return Array.from({ length: currentYear - startYear + 1 }, (_, i) => {
      const year = String(currentYear - i)
      return {
        label: year,
        value: year,
      }
    })
  }, [])

  const dynamicDays = useMemo(() => {
    if (!watchMonth || !watchYear) return formattedDays
    const monthIndex = months.indexOf(watchMonth)
    const yearNUm = parseInt(watchYear, 10)
    const maxDays = daysInMonth(new Date(yearNUm, monthIndex, 1))
    return Array.from({ length: maxDays }, (_, i) => {
      const d = String(i + 1)
      return {
        label: d,
        value: d,
      }
    })
  }, [formattedDays, watchMonth, watchYear])

  const handlePortfolioLinkChange = useCallback(
    (value: string, id: number) => {
      const linksCopy = [...links]
      const idx = linksCopy.findIndex(link => link.id === id)
      if (idx !== -1) {
        linksCopy[idx].url = value
        setLinks(linksCopy)

        setLinkErrors(prev => ({
          ...prev,
          [id]: isValidUrl(value) ? '' : t('errors.invalid_url'),
        }))
      }
    },
    [links, t]
  )

  const renderValue = (id: number) => {
    return links.find(link => link.id === id)?.url || ''
  }

  const toggleModal = (modal: Modals | undefined) => {
    setActiveModal(modal)
  }

  const onClose = async () => {
    setOpen(false)
  }

  const closeAccount = async () => {
    try {
      await closeContributorAccount()
      clearSession()
      cookie.remove(`eezy-cm-auth-token`)
      enqueueSnackbar(t('util.auth.account_closed'), {
        variant: 'success',
        autoHideDuration: 1500,
      })
      navigate('/')
    } catch (error) {
      enqueueSnackbar(t('util.something_went_wrong'), {
        variant: 'error',
      })
      captureError(
        error,
        'Error while closing account - Modals/ApplicationRejectedModal/index.tsx'
      )
    }
  }

  const onSubmitForm = useCallback(async () => {
    const formData = getValues()
    const formLinks = [...links]
    const portfolioLinks = formLinks
      .filter(link => link.url)
      .map(link => link.url)

    try {
      if (session?.id) {
        const data = { ...formData, portfolio_links: portfolioLinks }
        await updateContributorApplication(session.id, data)
        await onboardUser()
        toggleModal(Modals.ChangesSubmitted)
      }
    } catch (err) {
      enqueueSnackbar(t(getErrorMsg(err) || 'errors.requests.generic'), {
        variant: 'error',
      })
    }
  }, [
    enqueueSnackbar,
    getValues,
    links,
    onboardUser,
    session.id,
    t,
    updateContributorApplication,
  ])

  return (
    <Modal
      size={!activeModal ? 'larger' : 'medium'}
      modalOpen={isOpen}
      setModalOpen={onClose}
      handleClose={onClose}
      disableClose
    >
      {activeModal === Modals.ChangesSubmitted ? <ChangesSubmitted /> : null}
      {activeModal === Modals.CloseAccount ? (
        <CloseAccount
          toggleModal={toggleModal}
          closeAccount={closeAccount}
          closingAccount={closingAccount}
        />
      ) : null}
      {activeModal === undefined ? (
        loadingUserProfile ? (
          <S.FormLoadingContainer>
            <LoadingSpinner />
          </S.FormLoadingContainer>
        ) : (
          <>
            <Modal.Header>{t('application_rejected.modal.title')}</Modal.Header>
            <Modal.Body>
              <S.Container>
                <Typography variant="body1" textAlign="center">
                  {t('application_rejected.modal.content')}
                </Typography>

                {needsName ? (
                  <WarningContainer className="warning-container">
                    <WarningCircleIcon />
                    <WarningText>
                      {t('application_rejected.modal.warning')}
                    </WarningText>
                  </WarningContainer>
                ) : null}

                <S.FormSection>
                  <form onSubmit={handleSubmit(onSubmitForm)}>
                    {needsName ? (
                      <>
                        <S.FormRow>
                          <TextInput
                            fullWidth
                            required
                            error={!!errors.first_name}
                            helperText={errors.first_name?.message}
                            label={t('enrollment.personal.form.first_name')}
                            {...register('first_name', {
                              required: true,
                              pattern: {
                                value: /^[A-Za-zÀ-ÖØ-öø-ÿ' -]+$/,
                                message: t('errors.special_characters'),
                              },
                            })}
                          />
                          <TextInput
                            fullWidth
                            required
                            error={!!errors.last_name}
                            helperText={errors.last_name?.message}
                            label={t('enrollment.personal.form.last_name')}
                            {...register('last_name', {
                              required: true,
                              pattern: {
                                value: /^[A-Za-zÀ-ÖØ-öø-ÿ' -]+$/,
                                message: t('errors.special_characters'),
                              },
                            })}
                          />
                        </S.FormRow>
                        <S.FormSeparator />
                      </>
                    ) : null}
                    {needsAddress ? (
                      <>
                        <Typography variant="h6">
                          {t('enrollment.personal.form.address')}
                        </Typography>
                        <S.FormRow>
                          <TextInput
                            fullWidth
                            required
                            error={!!errors.address_street_line_1}
                            helperText={errors.address_street_line_1?.message}
                            label={t('enrollment.personal.form.street_address')}
                            {...register('address_street_line_1', {
                              required: true,
                            })}
                          />
                          <TextInput
                            fullWidth
                            required
                            error={!!errors.address_state}
                            helperText={errors.address_state?.message}
                            label={t('enrollment.personal.form.city_state')}
                            {...register('address_state', {
                              required: true,
                            })}
                          />
                        </S.FormRow>
                        <S.FormRow>
                          <Controller
                            name="country"
                            control={control}
                            render={({ field }) => (
                              <Select
                                fullWidth
                                required
                                label={t('enrollment.personal.form.country')}
                                selectOptions={formattedCountries}
                                {...field}
                                onChange={e => {
                                  const value = e.target.value
                                  setValue('country', value)
                                }}
                              />
                            )}
                          />
                          <TextInput
                            fullWidth
                            required
                            error={!!errors.address_postal_code}
                            helperText={errors.address_postal_code?.message}
                            label={t('enrollment.personal.form.postal_code')}
                            {...register('address_postal_code', {
                              required: true,
                            })}
                          />
                        </S.FormRow>
                        <S.FormSeparator />
                      </>
                    ) : null}

                    {needsBirthdate ? (
                      <>
                        <Typography variant="h6">
                          {t('enrollment.personal.form.date_of_birth')}
                        </Typography>
                        <S.FormRow>
                          <Controller
                            name="dob_month"
                            control={control}
                            render={({ field }) => (
                              <Select
                                fullWidth
                                required
                                placeholder={t('util.select')}
                                label={t('enrollment.personal.form.month')}
                                selectOptions={formattedMonths}
                                {...field}
                                onChange={e => {
                                  const value = e.target.value
                                  setValue('dob_month', value)
                                }}
                              />
                            )}
                          />
                          <Controller
                            name="dob_day"
                            control={control}
                            render={({ field }) => (
                              <Select
                                fullWidth
                                required
                                placeholder={t('util.select')}
                                label={t('enrollment.personal.form.day')}
                                selectOptions={dynamicDays}
                                {...field}
                                onChange={e => {
                                  const value = e.target.value
                                  setValue('dob_day', value)
                                }}
                              />
                            )}
                          />
                          <Controller
                            name="dob_year"
                            control={control}
                            render={({ field }) => (
                              <Select
                                fullWidth
                                required
                                placeholder={t('util.select')}
                                label={t('enrollment.personal.form.year')}
                                selectOptions={formattedYears}
                                {...field}
                                onChange={e => {
                                  const value = e.target.value
                                  setValue('dob_year', value)
                                }}
                              />
                            )}
                          />
                        </S.FormRow>

                        {errors.dob_year?.message ? (
                          <ErrorText>{errors.dob_year?.message}</ErrorText>
                        ) : null}

                        <S.FormSeparator />
                      </>
                    ) : null}

                    {needsPortfolioLink ? (
                      <Typography variant="h6">
                        {t('enrollment.professional.form.links')}
                      </Typography>
                    ) : null}
                    {needsPortfolioLink
                      ? links.map(link => (
                          <S.FormRow key={link.id}>
                            <TextInput
                              fullWidth
                              placeholder={t(
                                'enrollment.professional.form.portfolio_link'
                              )}
                              startAdornment={<PortfolioLinkIcon />}
                              value={renderValue(link.id)}
                              onChange={e =>
                                handlePortfolioLinkChange(
                                  e.target.value,
                                  link.id
                                )
                              }
                              error={Boolean(linkErrors[link.id])}
                              helperText={linkErrors[link.id]}
                            />
                          </S.FormRow>
                        ))
                      : null}
                    {needsPortfolioLink ? (
                      <Button
                        onClick={addAnotherLink}
                        disabled={totalLinksCount >= 10}
                        fullWidth
                      >
                        {t('enrollment.professional.form.add_another')}
                      </Button>
                    ) : null}
                    {totalLinksCount >= 10 ? (
                      <S.ErrorWrapper>
                        <ErrorText>
                          {t('enrollment.professional.form.max_links_reached')}
                        </ErrorText>
                      </S.ErrorWrapper>
                    ) : null}
                  </form>
                </S.FormSection>
              </S.Container>
            </Modal.Body>
            <Modal.Actions>
              <Button
                color="primary"
                onClick={onSubmitForm}
                disabled={
                  doesNotPassNameValidation() ||
                  doesNotPassAddressValidation() ||
                  doesNotPassDOBValidation() ||
                  doesNotPassLinkValidation() ||
                  updatingApplication
                }
                loading={updatingApplication || onboardingUser}
              >
                {t('util.submit')}
              </Button>
            </Modal.Actions>
            <S.FooterContainer>
              <Typography variant="body1" textAlign="center">
                {t('application_rejected.modal.close_account_info')}
                <span
                  className="close-your-account-link"
                  onClick={() => toggleModal(Modals.CloseAccount)}
                >
                  {t('application_rejected.modal.close_your_account')}
                </span>
              </Typography>
            </S.FooterContainer>
          </>
        )
      ) : null}
    </Modal>
  )
}

export default observer(ApplicationRejectedModal)
